import type { ProfileDisplaySummary } from './profileDisplaySummary';
import { resolveProfileRecord } from './profileDisplaySummary';
import { getStoredProfileDetailsHints } from './profileDetailsStorage';
import { getStoredProfileEmail, getStoredProfilePhone } from './profileContactStorage';
import { localBookingProfileWireSnapshot } from './profileBookingCompleteStorage';
import { mergePrivateClientRegisterEmptyPayload } from './privateClientRegisterForm';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import { extractProfileCountryId } from './profileAssetOwnerEdit';
import type { LastSignInMethod } from '../login/lastSignInMethod';
import { readLastSignInMethod } from '../login/lastSignInMethod';
import {
  isPhoneLiteRegisterSyntheticEmail,
  profileRecordUsesLiteRegisterPlaceholders,
  stripLiteRegisterPlaceholdersFromSnapshot,
} from './profileLitePlaceholders';

export type PrivateClientBookingField =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone_number'
  | 'birth_date'
  | 'occupation';

const BOOKING_REQUIRED_FIELDS: PrivateClientBookingField[] = [
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'birth_date',
];

/** Google / email sign-in already proved the inbox; phone and DOB are collected only for lite SMS profiles. */
const EMAIL_LOGIN_BOOKING_REQUIRED_FIELDS: PrivateClientBookingField[] = [
  'first_name',
  'last_name',
  'email',
];

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

/** Merge API profile + display hints (Google / JWT / local storage) for booking checks. */
export function mergePrivateClientBookingSnapshot(
  profileData: unknown,
  summary?: ProfileDisplaySummary | null,
  loginId?: string | null,
): Record<string, unknown> {
  const resolved = resolveProfileRecord(profileData);
  const merged: Record<string, unknown> = { ...(resolved ?? {}) };
  const storedDetails = getStoredProfileDetailsHints(loginId);
  const storedEmail = getStoredProfileEmail(loginId);
  const storedPhone = getStoredProfilePhone(loginId);
  const localBooking = localBookingProfileWireSnapshot(loginId);

  const applyString = (key: string, value: string | null | undefined) => {
    if (!pickString(merged[key]) && value?.trim()) {
      merged[key] = value.trim();
    }
  };

  if (summary) {
    applyString('first_name', summary.firstName);
    applyString('last_name', summary.lastName);
    applyString('email', summary.email);
    applyString('phone_number', summary.phone);
    applyString('birth_date', summary.dateOfBirth);
    applyString('occupation', summary.occupation);
  }

  applyString('first_name', storedDetails.firstName);
  applyString('last_name', storedDetails.lastName);
  applyString('occupation', storedDetails.occupation);
  applyString('birth_date', storedDetails.birthDate);
  applyString('email', storedEmail);
  applyString('phone_number', storedPhone);

  if (localBooking) {
    for (const [key, value] of Object.entries(localBooking)) {
      if (!pickString(merged[key]) && value.trim()) {
        merged[key] = value.trim();
      }
    }
  }

  return stripLiteRegisterPlaceholdersFromSnapshot(
    merged,
    pickString(merged.email) || loginId || summary?.email || null,
  );
}

function resolvedSignInMethod(explicit?: LastSignInMethod | null): LastSignInMethod | null {
  return explicit === undefined ? readLastSignInMethod() : explicit;
}

function googleSessionHasRealEmail(
  profileData: unknown,
  summary: ProfileDisplaySummary | null | undefined,
  loginId: string | null | undefined,
  signInMethod?: LastSignInMethod | null,
): boolean {
  if (resolvedSignInMethod(signInMethod) !== 'google') {
    return false;
  }
  const merged = mergePrivateClientBookingSnapshot(profileData, summary, loginId);
  const email = pickString(merged.email) || loginId?.trim() || summary?.email?.trim() || '';
  return email.includes('@') && !isPhoneLiteRegisterSyntheticEmail(email);
}

function bookingRequiredFields(
  profileData: unknown,
  loginId?: string | null,
): readonly PrivateClientBookingField[] {
  if (serverProfileStillLiteProvisioned(profileData)) {
    return BOOKING_REQUIRED_FIELDS;
  }
  const id = loginId?.trim() ?? '';
  if (id.includes('@') && !isPhoneLiteRegisterSyntheticEmail(id)) {
    return EMAIL_LOGIN_BOOKING_REQUIRED_FIELDS;
  }
  const record = resolveProfileRecord(profileData);
  const serverEmail = pickString(record?.email) || pickString(record?.Email);
  if (serverEmail.includes('@') && !isPhoneLiteRegisterSyntheticEmail(serverEmail)) {
    return EMAIL_LOGIN_BOOKING_REQUIRED_FIELDS;
  }
  return BOOKING_REQUIRED_FIELDS;
}

function fieldPresent(snapshot: Record<string, unknown>, field: PrivateClientBookingField): boolean {
  const value = pickString(snapshot[field]);
  if (!value) {
    return false;
  }
  if (field === 'email' && isPhoneLiteRegisterSyntheticEmail(value)) {
    return false;
  }
  return true;
}

/** True when GET /profile still has auto-provisioned placeholder names (not just a synthetic email). */
export function serverProfileStillLiteProvisioned(profileData: unknown): boolean {
  if (profileData == null) {
    return false;
  }
  const record = resolveProfileRecord(profileData);
  const email = pickString(record?.email) || pickString(record?.Email);
  return profileRecordUsesLiteRegisterPlaceholders(record, email);
}

/** Prefill private-client registration fields from an existing lite profile. */
export function privateClientCompletionPrefill(
  profileData: unknown,
  summary?: ProfileDisplaySummary | null,
  loginId?: string | null,
): Record<string, unknown> {
  const merged = mergePrivateClientBookingSnapshot(profileData, summary, loginId);
  const countryId = extractProfileCountryId(merged) ?? extractProfileCountryId(profileData);
  return mergePrivateClientRegisterEmptyPayload({
    email: pickString(merged.email) || '',
    password: '',
    confirm_password: '',
    country_id: countryId ?? ISRAEL_COUNTRY_ID,
    phone_number: pickString(merged.phone_number) || '',
    first_name: pickString(merged.first_name) || '',
    last_name: pickString(merged.last_name) || '',
    birth_date: pickString(merged.birth_date) || '',
    occupation: pickString(merged.occupation) || '',
  });
}

/** Fields still required before a private client can book an asset. */
export function missingPrivateClientFieldsForBooking(
  profileData: unknown,
  summary?: ProfileDisplaySummary | null,
  loginId?: string | null,
  signInMethod?: LastSignInMethod | null,
): PrivateClientBookingField[] {
  if (googleSessionHasRealEmail(profileData, summary, loginId, signInMethod)) {
    return [];
  }
  const merged = mergePrivateClientBookingSnapshot(profileData, summary, loginId);
  const missing = bookingRequiredFields(profileData, loginId).filter(
    (field) => !fieldPresent(merged, field),
  );
  const record = resolveProfileRecord(profileData);
  const serverEmail = pickString(record?.email) || pickString(record?.Email);
  if (isPhoneLiteRegisterSyntheticEmail(serverEmail) && !missing.includes('email')) {
    missing.push('email');
  }
  return missing;
}

export function isPrivateClientProfileReadyForBooking(
  profileData: unknown,
  summary?: ProfileDisplaySummary | null,
  loginId?: string | null,
  signInMethod?: LastSignInMethod | null,
): boolean {
  if (googleSessionHasRealEmail(profileData, summary, loginId, signInMethod)) {
    return true;
  }
  if (serverProfileStillLiteProvisioned(profileData)) {
    return false;
  }
  return missingPrivateClientFieldsForBooking(profileData, summary, loginId, signInMethod).length === 0;
}

export { buildPrivateClientProfileCompletionPatch } from './profileCompletionPatch';
